import { useTranslation } from 'react-i18next';
import type { User } from '../../auth';
import type { BaseFormSectionProps, ProjectUser } from '../types/project-form-types';

/**
 * Props para ProjectUserManagement
 */
interface ProjectUserManagementProps extends BaseFormSectionProps {
  availableUsers: User[];
  isLoadingUsers: boolean;
  onAddUser: () => void;
  onRemoveUser: (userId: string) => void;
  onChangeUserRole: (userId: string, newRole: 'viewer' | 'editor' | 'admin') => void;
}

/**
 * ProjectUserManagement - Gestión de usuarios del proyecto
 *
 * @remarks
 * Incluye:
 * - Selección de admin del proyecto (responsable)
 * - Lista de usuarios con acceso y sus roles
 * - Agregar/remover usuarios
 * - Cambiar roles de usuarios
 *
 * **Cumple con:**
 * - P1: Componente < 200 líneas
 * - P3: Documentación JSDoc completa
 */
export function ProjectUserManagement({
  formData,
  isLoading,
  availableUsers,
  isLoadingUsers,
  onFormDataChange,
  onAddUser,
  onRemoveUser,
  onChangeUserRole,
}: ProjectUserManagementProps): JSX.Element {
  const { t } = useTranslation('projects');

  /**
   * Obtiene información de un usuario por ID
   */
  const getUserById = (userId: string): User | undefined => {
    return availableUsers.find((u) => u.id === userId);
  };

  // Solo mostrar si hay tenant seleccionado
  if (!formData.tenantId) {
    return <></>;
  }

  return (
    <>
      {/* Admin del Proyecto */}
      <div className="form-section">
        <h2 className="form-section-title">{t('form.projectAdmin')}</h2>

        <div className="form-group">
          <label htmlFor="projectAdminId">
            {t('form.responsibleUser')} <span className="required">{t('form.required')}</span>
          </label>
          <select
            id="projectAdminId"
            value={formData.projectAdminId}
            onChange={(e) => onFormDataChange({ projectAdminId: e.target.value })}
            disabled={isLoading || isLoadingUsers}
            className="form-select"
          >
            <option value="">{t('form.selectUser')}</option>
            {availableUsers
              .filter((u) => u.role.name === 'admin')
              .map((adminUser) => (
                <option key={adminUser.id} value={adminUser.id}>
                  {`${adminUser.firstName} ${adminUser.lastName}`.trim()} ({adminUser.email})
                </option>
              ))}
          </select>
          <small className="form-help">{t('form.responsibleUserHelp')}</small>
        </div>
      </div>

      {/* Usuarios con Acceso */}
      <div className="form-section">
        <h2 className="form-section-title">{t('form.usersWithAccess')}</h2>

        <div className="form-group">
          <label>{t('form.addUser')}</label>
          <div
            style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 'var(--space-3)' }}
          >
            <select id="userToAdd" className="form-select" disabled={isLoadingUsers}>
              <option value="">{t('form.selectUser')}</option>
              {availableUsers.map((availUser) => (
                <option key={availUser.id} value={availUser.id}>
                  {`${availUser.firstName} ${availUser.lastName}`.trim()} ({availUser.email}) - {availUser.role.name}
                </option>
              ))}
            </select>

            <select id="userRole" className="form-select">
              <option value="viewer">{t('form.roleViewer')}</option>
              <option value="editor">{t('form.roleEditor')}</option>
              <option value="admin">{t('form.roleAdmin')}</option>
            </select>

            <button
              type="button"
              onClick={onAddUser}
              disabled={isLoadingUsers}
              className="btn-secondary"
              style={{ whiteSpace: 'nowrap' }}
            >
              {t('form.add')}
            </button>
          </div>
          <small className="form-help">{t('form.roleHelp')}</small>
        </div>

        {/* Lista de usuarios agregados */}
        {formData.projectUsers.length > 0 && (
          <div className="users-list">
            {formData.projectUsers.map((projectUser: ProjectUser) => {
              const userInfo = getUserById(projectUser.userId);
              return (
                <div key={projectUser.userId} className="user-item">
                  <div className="user-info">
                    <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                      {userInfo ? `${userInfo.firstName} ${userInfo.lastName}`.trim() : t('form.defaultUser')}
                    </span>
                    <span style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)' }}>
                      {userInfo?.email}
                    </span>
                    <select
                      value={projectUser.role}
                      onChange={(e) =>
                        onChangeUserRole(
                          projectUser.userId,
                          e.target.value as 'viewer' | 'editor' | 'admin'
                        )
                      }
                      className="form-select"
                      style={{ width: 'auto', minWidth: '120px' }}
                    >
                      <option value="viewer">{t('form.roleViewer')}</option>
                      <option value="editor">{t('form.roleEditor')}</option>
                      <option value="admin">{t('form.roleAdmin')}</option>
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={() => onRemoveUser(projectUser.userId)}
                    className="btn-remove"
                    title={t('form.remove')}
                  >
                    ✕
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {formData.projectUsers.length === 0 && (
          <p className="empty-text">{t('form.noUsers')}</p>
        )}
      </div>
    </>
  );
}
